import type { CitationOutput } from "@/components/tool-kit/source-citation-builder";
import {
  apaAuthors,
  type CitationParts,
  chicagoBibliographyAuthors,
  chicagoNoteAuthors,
  chicagoShortAuthors,
  mlaAuthors,
  mlaDate,
  mlaInTextAuthors,
  type PersonName,
  parsePartialDate,
  tidyParts,
} from "@/lib/tool-kit/citation-parts";
import {
  capitalizeSubtitle,
  closer,
  get,
  italic,
  mainTitle,
  mlaUrl,
  plain,
  titleCase,
} from "@/lib/tool-kit/legal-sources/common";
import {
  apaContributors,
  apaInText,
  type Contributors,
  chicagoBibliographyContributors,
  chicagoNoteContributors,
  mlaContributors,
  parsePeople,
  publisherName,
  type SpecialValues,
} from "@/lib/tool-kit/special-sources/common";

/*
 * A classic or republished book — a modern reprint of Austen or Darwin, or a free
 * online edition such as Project Gutenberg — in APA 7, MLA 9 and Chicago 18.
 *
 * Sources checked (October 2026):
 * - APA 7: "Book/ebook references", example 3, "Republished book, with editor" —
 *   "Watson, J. B., & Rayner, R. (2013). Conditioned emotional reactions: The case of
 *   Little Albert (D. Webb, Ed.). CreateSpace Independent Publishing Platform.
 *   https://a.co/06Se6Na (Original work published 1920)"; the republication year is
 *   the main date, the original year closes the reference, and both appear in the
 *   text, earlier first: (Watson & Rayner, 1920/2013). An ebook with a stable URL
 *   gives the URL after the publisher.
 * - MLA 9: MLA Style Center, "reprinted books" — the original year may follow the
 *   title as a supplemental element: "Achebe, Chinua. Things Fall Apart. 1959.
 *   Penguin Books, 2017." For a free online edition, MLA Style Center (July 22, 2026)
 *   gives the original facts and then the site as a second container: "Wood, Ellen
 *   … Anne Hereford: A Novel. Tinsley Brothers, 1871. Project Gutenberg, 9 Sept.
 *   2018, www.gutenberg.org/…". The in-text citation is author and page.
 * - Chicago 18 §14.16 ("Reprint editions and modern editions"): "Ralph Ellison,
 *   Invisible Man (Random House, 1952; repr., Vintage Books, 1995), 242–43." — the
 *   bibliography form "Random House, 1952. Reprint, Vintage Books, 1995." follows the
 *   CSL implementation of §14.16; with no original publisher, the CMOS Q&A writes
 *   "(1904; repr., Government Printing Office, 1972)". Chicago also accepts a note of
 *   the history at the end — "First published …" (CMOS Q&A: "First published in
 *   French as . . ."). No place of publication for books since 1900 (§14.30).
 * - Example facts: Pride and Prejudice was first published by Thomas Egerton in 1813;
 *   Project Gutenberg eBook #1342 was released June 1, 1998.
 */

export const REPUBLISHED_BOOK_FIELDS = {
  authors: "authors",
  title: "title",
  translators: "translators",
  editors: "editors",
  originalYear: "originalYear",
  originalPublisher: "originalPublisher",
  publisher: "publisher",
  year: "year",
  site: "site",
  posted: "posted",
  url: "url",
} as const;

const F = REPUBLISHED_BOOK_FIELDS;

export type RepublishedEdition = "print" | "online";

export interface RepublishedBookLabels {
  apa: string;
  mla: string;
  chicago: string;
  referenceList: string;
  parenthetical: string;
  narrative: string;
  worksCited: string;
  inText: string;
  footnote: string;
  shortNote: string;
  bibliography: string;
  bibliographyFirstPublished: string;
  apaNote: string;
  mlaNote: string;
  chicagoNote: string;
}

interface Book {
  authors: PersonName[];
  title: string;
  contributors: Contributors;
  originalYear: string;
  originalPublisher: string;
  online: boolean;
  /** Print: the publisher. Online: the site, such as Project Gutenberg. */
  source: string;
  /** Print: the year of the edition. Online: the date the site posted it. */
  date: string;
  url: string;
}

function readBook(values: SpecialValues, edition: RepublishedEdition): Book {
  const online = edition === "online";
  return {
    authors: parsePeople(get(values, F.authors)),
    title: get(values, F.title),
    contributors: {
      editors: parsePeople(get(values, F.editors)),
      translators: parsePeople(get(values, F.translators)),
    },
    originalYear: get(values, F.originalYear),
    originalPublisher: get(values, F.originalPublisher),
    online,
    source: get(values, online ? F.site : F.publisher),
    date: get(values, online ? F.posted : F.year),
    url: get(values, F.url),
  };
}

export function republishedBookIsReady(values: SpecialValues): boolean {
  return get(values, F.title) !== "";
}

/** The year of the edition used: "2003" from "2003" or "1998-06-01". */
function editionYear(book: Book): string {
  const date = parsePartialDate(book.date);
  return date ? String(date.year) : book.date || "n.d.";
}

/* ------------------------------------------------------------------- APA */

function apaOutput(book: Book, labels: RepublishedBookLabels): CitationOutput {
  const year = editionYear(book);
  const authors = apaAuthors(book.authors);
  const title = capitalizeSubtitle(book.title);
  const contributors = apaContributors(book.contributors);
  const source = book.source
    ? `${publisherName(book.source, "apa")}${closer(book.source)}`
    : "";
  const tail = [
    source,
    book.url,
    book.originalYear ? `(Original work published ${book.originalYear})` : "",
  ]
    .filter(Boolean)
    .join(" ");
  const titlePart: CitationParts = [
    italic(title),
    plain(contributors ? ` (${contributors}).` : closer(title)),
  ];
  const reference: CitationParts = authors
    ? [
        plain(`${authors}${closer(authors)} (${year}). `),
        ...titlePart,
        plain(` ${tail}`),
      ]
    : [...titlePart, plain(` (${year}). ${tail}`)];
  const dates = book.originalYear ? `${book.originalYear}/${year}` : year;
  const inText = (narrative: boolean): CitationParts => {
    if (book.authors.length > 0) {
      return [plain(apaInText(book.authors, dates, narrative))];
    }
    const short = titleCase(mainTitle(book.title));
    return narrative
      ? [italic(short), plain(` (${dates})`)]
      : [plain("("), italic(short), plain(`, ${dates})`)];
  };
  return {
    key: "apa",
    label: labels.apa,
    entries: [
      { label: labels.referenceList, parts: tidyParts(reference) },
      { label: labels.parenthetical, parts: inText(false) },
      { label: labels.narrative, parts: inText(true) },
    ],
    note: labels.apaNote,
  };
}

/* ------------------------------------------------------------------- MLA */

/** Online: the original publication, then the site as a second container. */
function mlaOnlineTail(book: Book, contributors: string): CitationParts {
  const tail: CitationParts = [];
  const original = [book.originalPublisher, book.originalYear]
    .filter(Boolean)
    .join(", ");
  if (original) {
    tail.push(plain(` ${original}.`));
  }
  if (contributors) {
    tail.push(plain(` ${contributors}.`));
  }
  const date = parsePartialDate(book.date);
  const location = [date ? mlaDate(date) : book.date, mlaUrl(book.url)]
    .filter(Boolean)
    .join(", ");
  if (book.source) {
    tail.push(plain(" "), italic(book.source));
    tail.push(plain(location ? `, ${location}.` : "."));
  } else if (location) {
    tail.push(plain(` ${location}.`));
  }
  return tail;
}

/** Print: the optional original year, then the edition's publication facts. */
function mlaPrintTail(book: Book, contributors: string): CitationParts {
  const tail: CitationParts = [];
  if (book.originalYear) {
    tail.push(plain(` ${book.originalYear}.`));
  }
  const publication = [
    contributors,
    book.source ? publisherName(book.source, "mla") : "",
    book.date,
    book.url ? mlaUrl(book.url) : "",
  ].filter(Boolean);
  if (publication.length > 0) {
    tail.push(plain(` ${publication.join(", ")}.`));
  }
  return tail;
}

function mlaOutput(book: Book, labels: RepublishedBookLabels): CitationOutput {
  const authors = mlaAuthors(book.authors);
  const title = titleCase(book.title);
  const contributors = mlaContributors(book.contributors);
  const entry: CitationParts = [
    plain(authors ? `${authors}${closer(authors)} ` : ""),
    italic(title),
    plain(closer(title)),
    ...(book.online
      ? mlaOnlineTail(book, contributors)
      : mlaPrintTail(book, contributors)),
  ];
  const who = mlaInTextAuthors(book.authors);
  const inText: CitationParts = who
    ? [plain(`(${who})`)]
    : [plain("("), italic(titleCase(mainTitle(book.title))), plain(")")];
  return {
    key: "mla",
    label: labels.mla,
    entries: [
      { label: labels.worksCited, parts: tidyParts(entry) },
      { label: labels.inText, parts: inText },
    ],
    note: labels.mlaNote,
  };
}

/* --------------------------------------------------------------- Chicago */

/** "T. Egerton, 1813" — or the year alone when the first publisher is not known. */
function originalFacts(book: Book): string {
  return [book.originalPublisher, book.originalYear].filter(Boolean).join(", ");
}

/** The parenthesis of a full note: "(T. Egerton, 1813; repr., Penguin Books, 2003)". */
function chicagoNoteFacts(book: Book): string {
  const original = originalFacts(book);
  if (book.online) {
    return original ? ` (${original})` : "";
  }
  const current = currentFacts(book);
  if (original && current) {
    return ` (${original}; repr., ${current})`;
  }
  return original || current ? ` (${original || current})` : "";
}

/** "2003" — or nothing when the edition's year is not known. */
function knownYear(book: Book): string {
  const year = editionYear(book);
  return year === "n.d." ? "" : year;
}

/** "Penguin Books, 2003" — the edition you used. */
function currentFacts(book: Book): string {
  const publisher = book.source ? publisherName(book.source, "chicago") : "";
  return [publisher, knownYear(book)].filter(Boolean).join(", ");
}

function chicagoNote(book: Book): CitationParts {
  const authors = chicagoNoteAuthors(book.authors);
  const contributors = chicagoNoteContributors(book.contributors);
  const online = book.online && book.source ? `, ${book.source}` : "";
  const link = book.url ? `, ${book.url}` : "";
  return [
    plain(authors ? `${authors}, ` : ""),
    italic(titleCase(book.title)),
    plain(
      `${contributors ? `, ${contributors}` : ""}${chicagoNoteFacts(book)}${online}${link}.`
    ),
  ];
}

function chicagoShortNote(book: Book): CitationParts {
  const authors = chicagoShortAuthors(book.authors);
  return [
    plain(authors ? `${authors}, ` : ""),
    italic(titleCase(mainTitle(book.title))),
    plain("."),
  ];
}

/** Author. Title. Contributors. — the part every bibliography form shares. */
function chicagoBibliographyHead(book: Book): CitationParts {
  const authors = chicagoBibliographyAuthors(book.authors);
  const title = titleCase(book.title);
  const contributors = chicagoBibliographyContributors(book.contributors);
  return [
    plain(authors ? `${authors}${closer(authors)} ` : ""),
    italic(title),
    plain(`${closer(title)}${contributors ? ` ${contributors}` : ""}`),
  ];
}

/** "T. Egerton, 1813. Reprint, Penguin Books, 2003." or, online, the original and the site. */
function chicagoHistory(book: Book): string {
  const original = originalFacts(book);
  if (book.online) {
    const site = book.source ? ` ${book.source}.` : "";
    return `${original ? ` ${original}.` : ""}${site}`;
  }
  const current = currentFacts(book);
  if (original && current) {
    return ` ${original}. Reprint, ${current}.`;
  }
  return original || current ? ` ${original || current}.` : "";
}

/** "Penguin Books, 2003. First published 1813 by T. Egerton." */
function chicagoFirstPublished(book: Book): string {
  const current = currentFacts(book);
  const by = book.originalPublisher ? ` by ${book.originalPublisher}` : "";
  return `${current ? ` ${current}.` : ""} First published ${book.originalYear}${by}.`;
}

function chicagoOutput(
  book: Book,
  labels: RepublishedBookLabels
): CitationOutput {
  const link = book.url ? ` ${book.url}.` : "";
  const head = chicagoBibliographyHead(book);
  const entries = [
    { label: labels.footnote, parts: tidyParts(chicagoNote(book)) },
    { label: labels.shortNote, parts: tidyParts(chicagoShortNote(book)) },
    {
      label: labels.bibliography,
      parts: tidyParts([...head, plain(`${chicagoHistory(book)}${link}`)]),
    },
  ];
  if (!book.online && book.originalYear) {
    entries.push({
      label: labels.bibliographyFirstPublished,
      parts: tidyParts([
        ...head,
        plain(`${chicagoFirstPublished(book)}${link}`),
      ]),
    });
  }
  return {
    key: "chicago",
    label: labels.chicago,
    entries,
    note: labels.chicagoNote,
  };
}

export function formatRepublishedBook(
  values: SpecialValues,
  edition: RepublishedEdition,
  labels: RepublishedBookLabels
): CitationOutput[] {
  const book = readBook(values, edition);
  return [
    apaOutput(book, labels),
    mlaOutput(book, labels),
    chicagoOutput(book, labels),
  ];
}
